import React, { useState, useEffect } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import SearchPinIcon from '../../../../../public/static/images/new_ad/icons/icon-select-location.svg';
import MetroIcon from '../../../../../public/static/images/new_ad/icons/icon-metro.svg';
import { getGoogleApiKey } from '../../../../../utils/helpers/common';
import { FORM_VALUES } from '../../../../../utils/helpers/constants';
import Icon from '../../../../Icon';
import { useLandmarks } from '../../../../../hooks/useLandmarks';
import { InitCoordinatesType } from '../types';
import {
  chipsStyles,
  closeButtonStyles,
  LandmarksContainer,
  MapContainer,
  mapModalStyles,
  MapOverlay,
  saveButtonStyles,
  SelectMapButton,
  SelectMapButtonText
} from './styled';
import { FormErrorField } from '../FormErrorField';
import { Modal } from '../../../../ui/Modal';
import { Button } from '../../../../ui/Button';
import { ButtonType } from '../../../../../styled/theme/types';
import { CloseIcon } from '../../../../../icons';
import { palette } from '../../../../../styles/_palette';
import { Chips } from '../../../../ui/Chips';
import { ChipsTheme } from '../../../../ui/Chips/types';
import { Map } from './Map';

interface SelectLocationMapProps {
  initialLocation: InitCoordinatesType;
}

export const SelectLocationMap: React.FC<SelectLocationMapProps> = ({
  initialLocation = { lat: 40.409264, lng: 49.867092 }
}) => {
  const { t } = useTranslation();
  const { change } = useForm();
  const { values } = useFormState();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: getGoogleApiKey()
  });

  const [pinSelected, setPinSelected] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [coordinates, setCoordinates] = useState(initialLocation);

  const { handleLocationDelete, getLandmarks, locations } =
    useLandmarks(coordinates);

  useEffect(() => {
    change(
      FORM_VALUES.locationIds,
      locations?.map(location => location.id)
    );
  }, [locations]);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const saveCoordinates = () => {
    setPinSelected(true);
    change(FORM_VALUES.coordinates, coordinates);
    getLandmarks();
    toggleModal();
  };

  useEffect(() => {
    const hasCoordinatesChanged =
      coordinates.lat !== initialLocation.lat ||
      coordinates.lng !== initialLocation.lng;

    if (hasCoordinatesChanged && !pinSelected) {
      setCoordinates(initialLocation);
    }
  }, [initialLocation, pinSelected]);

  const handleBack = () => {
    if (values[FORM_VALUES.coordinates]) {
      setCoordinates(values[FORM_VALUES.coordinates]);
    }
    toggleModal();
  };

  const handleContainerClick = isModalOpen || !pinSelected ? null : toggleModal;

  const mapComponent = (
    <Map
      isModalOpen={isModalOpen}
      coordinates={coordinates}
      initialLocation={initialLocation}
      pinSelected={pinSelected}
      setCoordinates={setCoordinates}
    />
  );

  return (
    <div>
      <MapContainer
        tabIndex={0}
        role="button"
        onClick={handleContainerClick}
        onKeyPress={handleContainerClick}
        data-cy="item-form-map"
      >
        {isLoaded && mapComponent}
        {isLoaded && !pinSelected && !isModalOpen && (
          <MapOverlay>
            <SelectMapButton type="button" onClick={toggleModal}>
              <Icon
                width={20}
                height={20}
                IconComponent={SearchPinIcon}
                indent={false}
              />
              <SelectMapButtonText tag="body_1">
                {t('new_ad.select_on_map')}
              </SelectMapButtonText>
            </SelectMapButton>
          </MapOverlay>
        )}
      </MapContainer>
      <Modal
        isOpen={isModalOpen}
        onClose={handleBack}
        modalId="item-form-map-modal"
        modalStyles={mapModalStyles}
      >
        <Button
          onClick={handleBack}
          state={ButtonType.white}
          styles={closeButtonStyles}
        >
          <CloseIcon fill={palette.heatheredGrey} width={15} height={15} />
        </Button>
        <Button
          onClick={saveCoordinates}
          text={t('new_ad.save')}
          styles={saveButtonStyles}
        />
        {isLoaded && mapComponent}
      </Modal>

      <FormErrorField
        field={FORM_VALUES.coordinates}
        cypress="coordinates-field-error"
      />

      {!!locations?.length && (
        <LandmarksContainer data-cy="landmarks-container">
          {locations?.map(location => (
            <Chips
              key={location.id}
              text={location.name || ''}
              styles={chipsStyles}
              chipsTheme={ChipsTheme.White}
              icon={location.locationGroupId === '1' ? MetroIcon : null}
              resetHandler={() => handleLocationDelete(location.id)}
              cypress={`landmarks-${location.id}`}
              isTextPadding={false}
              dirty
            />
          ))}
        </LandmarksContainer>
      )}
    </div>
  );
};
